/* eslint-disable no-unused-vars */
import React from "react";
import Modal from "./Modal";
import { Building, Close, SupplierIcon } from "../icons";
import { Building2, Package, PenLine } from "lucide-react";

function formatDate(date) {
  if (!date) return "N/A";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

function formatPrice(price) {
  if (price == null || price === "") return "N/A";
  return `Rs ${Number(price).toLocaleString()}`;
}

function getInventoryDetails(inventoryItem = {}) {
  const item = inventoryItem?.itemId || {};
  const campus = inventoryItem?.campusId || {};
  const vendor = item?.itemVendor || inventoryItem?.vendor || {};
  const category = item?.itemCategory  || {};

  const initializedBy = inventoryItem?.createdBy || item?.createdBy;
  const auditedBy = inventoryItem?.lastModifiedQuantityBy;

  return {
    itemName: item?.name  || "N/A",
    itemImage: item?.itemPicture?.secure_url  || "",
    categoryName: category?.name || "N/A",
    status: item?.status || "",
    campusName: campus?.name || "N/A",
    campusAddress: campus?.address || "",
    vendorName: vendor?.name || "N/A",
    vendorAddress: vendor?.address || "",
    price: item?.itemPrice,
    stock: inventoryItem?.stock  ?? 0,
    initializedBy: {
      name: initializedBy?.name || "N/A",
      email: initializedBy?.email || "N/A",
    },
    initializedAt: inventoryItem?.createdAt,
    auditedBy: {
      name: auditedBy?.name || "N/A",
      email: auditedBy?.email || "N/A",
    },
    auditedAt: inventoryItem?.updatedAt,
  };
}

function UserAuditCard({ label, user, date }) {
  return (
    <div className="rounded-[10px]  bg-[#FAFAFA] px-4 pt-5 pb-9">
      <p className="mb-2.5 leading-[9px] text-[10px] text-[#8E8E8E]">{label}</p>
      <div className="flex items-start gap-[5px]">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#D9D9D9]" />
        <div className="min-w-0">
          <p className="text-sm leading-[9px] font-bold text-black">{user.name}</p>
          <p className="mt-[5px] truncate text-[10px] text-[#8E8E8E] leading-[9px]">{user.email}</p>
          <p className="mt-[3px] text-[8px] text-[#8E8E8E] leading-[9px]">{formatDate(date)}</p>
        </div>
      </div>
    </div>
  );
}

export default function CentralInventoryDetailsModal({
  open,
  onClose,
  inventoryItem = {},
  isLoading = false,
}) {
  const details = getInventoryDetails(inventoryItem);
  console.log("Inventory Item Details:", inventoryItem); // Debug log to check the details being rendered
  const statusLabel =
    details.status === "added"
      ? "Added"
      : details.status === "notadded"
        ? "Not Added"
        : details.status;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="max-w-[620px] px-0 pt-5 pb-7  overflow-hidden"
      centered={false}
    >
      <div className="w-full bg-white">
        <div className="flex items-start justify-between border-b border-[#E5E5E5] px-7 pb-3">
          <div>
            <p className="text-xl font-bold text-black leading-6">
              Inventory Analysis
            </p>
            <p className="text-sm text-[#8E8E8E] leading-5">
              Full 360-Degree Stock Review
            </p>
          </div>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <div className="px-[30px] pt-5">
          {isLoading ? (
            <div className="rounded-[16px] border border-[#ECECEC] bg-[#FAFAFA] p-8 text-center">
              <p className="text-sm text-[#6B7280]">Loading item details...</p>
            </div>
          ) : (
            <div className="">
              <div className="rounded-[10px]  bg-[#FAFAFA] px-8 py-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-[97px] w-[89px] shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#6D4E8A]">
                    {details.itemImage ? (
                      <img
                        src={details.itemImage}
                        alt={details.itemName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <PenLine
                        className="text-white"
                        size={32}
                        strokeWidth={1.5}
                      />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#8E8E8E] leading-4">
                      Asset Name
                    </p>
                    <p className="text-base font-bold capitalize text-black leading-5">
                      {details.itemName}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-2.5">
                      <span className="rounded-full bg-[#D0FAFF] px-4 leading-6 text-xs  text-[#228E85]">
                        {details.categoryName}
                      </span>
                      <span className="rounded-full bg-[#E5FFF6] px-4 leading-6 text-xs  text-[#2C765C]">
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-2.5 sm:grid-cols-2">
                <div className="rounded-[10px]  bg-[#FAFAFA] p-6">
                  <div className="mb-2.5 flex items-center gap-2.5 text-[#147578]">
                    <Building />
                    <p className="text-sm leading-6">Warehouse</p>
                  </div>
                  <p className="text-sm leading-4 text-black">
                    {details.campusName}
                  </p>
                  {details.campusAddress && (
                    <p className="mt-3 text-[10px] leading-5 text-[#8E8E8E]">
                      {details.campusAddress}
                    </p>
                  )}
                </div>

                <div className="rounded-[10px]  bg-[#FAFAFA] p-6">
                  <div className="mb-2.5 flex items-center gap-2.5 text-[#147578]">
                    <SupplierIcon />
                    <p className="text-sm leading-6">Supplier</p>
                  </div>
                  <p className="text-sm leading-4 text-black">
                    {details.vendorName}
                  </p>
                  {details.vendorAddress && (
                    <p className="mt-3 text-[10px] leading-3 text-[#8E8E8E]">
                      {details.vendorAddress}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-2.5 mt-2.5 sm:grid-cols-2">
                <div className="rounded-[10px]  bg-[#FAFAFA] p-7">
                  <p className="mb-1 text-[10px] leading-[14px] text-[#8E8E8E]">Inventory Price</p>
                  <p className="text-sm font-bold leading-4 text-black">
                    {formatPrice(details.price)}
                  </p>
                </div>

                <div className="rounded-[10px] bg-[#6D4E8A] p-7">
                  <p className="mb-2 text-[10px] leading-[14px] text-white">Current Stock</p>
                  <p className="  text-white leading-4 text-[10px]">
                    <span className="font-bold text-sm">{details.stock}</span> Units Available
                  </p>
                </div>
              </div>

              <div className="grid mt-5 gap-2.5 sm:grid-cols-2">
                <UserAuditCard
                  label="Initialized By"
                  user={details.initializedBy}
                  date={details.initializedAt}
                />
                <UserAuditCard
                  label="Last Audited By"
                  user={details.auditedBy}
                  date={details.auditedAt}
                />
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-5 w-full rounded-[10px] bg-[#6D4E8A] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#5a3f75]"
          >
            Close Record Analysis
          </button>
        </div>
      </div>
    </Modal>
  );
}
